// src/app/core/session.service.ts
import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AffluenceThresholdsService, AffluenceThresholds } from './services/affluence-thresholds.service';

export interface EstablishmentBrief {
  id: string;
  name: string;
  tz?: string;
  org_id?: string;
}

export interface MeResponse {
  establishments?: Array<{ id: string; name: string; tz?: string; org_id?: string }>;
  establishment_active_id?: string | null;
}

export interface ScanContext {
  id: string;
  name: string;
}

export interface QueueSnapshot {
  id: string;
  name: string;
  scan_contexts?: ScanContext[];
  patient_message?: string | null;
  status?: string | null;
  /** Temps d'attente courant (minutes), déjà renvoyé par GET /api/v1/queues. */
  averageTodayMin?: number;
  /** Palier Fluide/Modéré/Forte affluence précalculé côté backend : 20/50=fluide/modéré, 80/100=forte affluence. */
  level?: number;
  /** File réellement ouverte maintenant (statut + horaires établissement). */
  open?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly api = (environment.apiUrl ?? '').replace(/\/+$/, '');
  private readonly STORAGE_KEY = 'waitora_selected_establishment';
  private lastFetchTime = 0;

  /** Récupère l'établissement sauvegardé depuis le localStorage */
  private getSavedEstablishment(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch {
      return null;
    }
  }

  /** Liste d'établissements visibles pour l'utilisateur courant */
  readonly establishments = signal<EstablishmentBrief[]>([]);
  /** Établissement sélectionné (id) */
  readonly selectedId = signal<string | null>(this.getSavedEstablishment());
  /** Queues de l'établissement sélectionné */
  readonly queues = signal<QueueSnapshot[]>([]);
  /** Seuils Fluide/Modéré/Forte affluence effectifs (défaut org ou surcharge) de l'établissement sélectionné */
  readonly affluenceThresholds = signal<AffluenceThresholds | null>(null);
  /** Version des données (bump à chaque modif pour forcer l'accueil à se recharger) */
  readonly dataVersion = signal(0);
  /** Cache des promesses en cours pour éviter les appels simultanés */
  private nowPromises = new Map<string, Promise<any>>();
  private readonly affluenceThresholdsSvc = inject(AffluenceThresholdsService);

  constructor(private http: HttpClient) {}

  /** Recharge les seuils d'affluence de l'établissement (appelé à chaque changement de site sélectionné). */
  private fetchAffluenceThresholdsFor(establishmentId: string): void {
    if (!establishmentId) {
      this.affluenceThresholds.set(null);
      return;
    }
    this.affluenceThresholdsSvc.getForEstablishment(establishmentId).subscribe({
      next: d => this.affluenceThresholds.set(d.thresholds),
      error: err => {
        console.warn('[session] affluence-thresholds GET error', err);
        this.affluenceThresholds.set(null);
      },
    });
  }

  /** Récupère les données now pour une queue (sans cache, uniquement dédoublonnage) */
  getNowData(queueId: string): Promise<any> {
    // Si un appel est déjà en cours, partager la promesse
    if (this.nowPromises.has(queueId)) {
      return this.nowPromises.get(queueId)!;
    }
    
    // Créer un nouvel appel
    const promise = this.http.get(`${this.api}/api/v1/queues/${queueId}/now`).toPromise()
      .then(data => {
        this.nowPromises.delete(queueId);
        return data;
      })
      .catch(err => {
        this.nowPromises.delete(queueId);
        throw err;
      });
    
    this.nowPromises.set(queueId, promise);
    return promise;
  }

  /** Incrémente la version locale des données (observée par l'accueil) */
  bumpDataVersion(): void {
    this.dataVersion.set(this.dataVersion() + 1);
  }

  /** Sauvegarde l'établissement sélectionné dans le localStorage */
  private saveSelectedEstablishment(id: string | null): void {
    if (id) {
      localStorage.setItem(this.STORAGE_KEY, id);
    } else {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /** Charge /auth/me et hydrate les signals */
  loadMe(): void {
    this.http.get<MeResponse>(`${this.api}/api/v1/auth/me`).subscribe({
      next: (me) => this.applyMe(me),
      error: (err) => {
        console.error('[session] /auth/me error', err);
        this.establishments.set([]);
        this.selectedId.set(null);
        this.queues.set([]);
      },
    });
  }

  /**
   * Hydrate les signals à partir d'une réponse /auth/me déjà récupérée (ex: par AuthService.bootstrap()),
   * sans refaire un second appel réseau. Mêmes effets de bord que loadMe(), sans le GET.
   */
  hydrateFromAuthMe(me: MeResponse): void {
    this.applyMe(me);
  }

  private applyMe(me: MeResponse): void {
    const arr: EstablishmentBrief[] = Array.isArray(me?.establishments)
      ? me!.establishments!
        .filter((e) => !!e && !!e.id && !!e.name)
        .map((e) => ({ id: String(e.id), name: String(e.name), tz: e.tz, org_id: e.org_id }))
      : [];

    this.establishments.set(arr);

    // Récupération des candidats
    const saved = this.getSavedEstablishment();
    const active = me?.establishment_active_id ?? null;
    const first = arr.length ? arr[0].id : null;

    // Logique de sélection avec priorité explicite
    let chosen: string | null = null;

    // 1. Priorité au localStorage si valide
    if (saved && arr.find(x => x.id === saved)) {
      chosen = saved;
    }
    // 2. Sinon, établissement actif du serveur si valide
    else if (active && arr.find(x => x.id === active)) {
      chosen = active;
    }
    // 3. Sinon, premier de la liste
    else {
      chosen = first;
    }

    // Ne changer selectedId que si c'est différent pour éviter la réinitialisation visuelle
    if (this.selectedId() !== chosen) {
      this.selectedId.set(chosen);
    }
    this.queues.set([]);

    if (chosen) {
      // Sauvegarder le choix final
      this.saveSelectedEstablishment(chosen);
      // aligne le contexte serveur + charge les queues
      this.putContext(chosen).subscribe({
        next: () => this.fetchQueuesFor(chosen),
        error: () => this.fetchQueuesFor(chosen), // on affiche quand même
      });
      this.fetchAffluenceThresholdsFor(chosen);
    }
  }

  /**
   * PUT /api/context (persist selection côté back)
   * ⚠️ corps en **camelCase** pour coller au DTO côté back:
   *   { establishmentId: '...', queueId?: '...' }
   */
  private putContext(establishmentId: string, queueId?: string | null) {
    const body: any = { establishmentId };
    if (queueId) body.queueId = queueId;
    return this.http.put(`${this.api}/api/context`, body);
  }

  /**
   * Récupère les queues de l'établissement.
   * Stratégie sans régression :
   *   1) /api/v1/queues?establishmentId=...
   *   2) (fallback) /api/v1/establishments/{id}/queues
   */
  fetchQueuesFor(establishmentId: string) {
    if (!establishmentId) {
      this.queues.set([]);
      return;
    }
    
    const now = Date.now();
    if (now - this.lastFetchTime < 500) {
      return; // Éviter les appels trop rapprochés
    }
    
    this.lastFetchTime = now;

    const mapQueues = (raw: any[]): QueueSnapshot[] => {
      const mapped = (Array.isArray(raw) ? raw : [])
        .map((q: any) => {
          const id =
            q?.id ?? q?.queueId ?? q?.queue_id ?? '';
          const name =
            q?.name ?? q?.label ?? q?.queueName ?? 'File';
          const scan_contexts = Array.isArray(q?.scan_contexts)
            ? q.scan_contexts.map((ctx: any) => ({
                id: String(ctx?.id ?? ''),
                name: String(ctx?.name ?? '')
              })).filter((ctx: any) => ctx.id && ctx.name)
            : [];
          const patient_message = q?.patient_message ?? q?.patientMessage ?? null;
          const status = q?.status ?? q?.queueStatus ?? null;
          const averageTodayMin = q?.averageTodayMin ?? q?.average_today_min;
          const level = q?.level;
          const open = q?.open;
          return id ? { id: String(id), name: String(name), scan_contexts, patient_message, status, averageTodayMin, level, open } : null;
        })
        .filter((x): x is NonNullable<typeof x> => x !== null);
      return mapped;
    };

    // Variante A (historique)
    const params = new HttpParams().set('establishmentId', establishmentId);
    this.http.get<any[]>(`${this.api}/api/v1/queues`, { params }).subscribe({
      next: (qsA) => {
        const mappedA = mapQueues(qsA);
        if (mappedA.length > 0) {
          this.queues.set(mappedA);
          return;
        }
        // Fallback si vide
        this.http
          .get<any[]>(`${this.api}/api/v1/establishments/${encodeURIComponent(establishmentId)}/queues`)
          .subscribe({
            next: (qsB) => this.queues.set(mapQueues(qsB)),
            error: (errB) => {
              console.error('[session] queues GET (fallback) error', errB);
              this.queues.set([]);
            },
          });
      },
      error: (errA) => {
        console.warn('[session] queues GET (variant A) error', errA);
        // Fallback si erreur
        this.http
          .get<any[]>(`${this.api}/api/v1/establishments/${encodeURIComponent(establishmentId)}/queues`)
          .subscribe({
            next: (qsB) => this.queues.set(mapQueues(qsB)),
            error: (errB) => {
              console.error('[session] queues GET (fallback) error', errB);
              this.queues.set([]);
            },
          });
      },
    });
  }

  /** Change l'établissement actif côté front + back, puis recharge les queues */
  selectEstablishment(id: string): void {
    if (!id) return;
    if (this.selectedId() === id) {
      // Même si c'est le même ID, s'assurer qu'il est sauvegardé
      this.saveSelectedEstablishment(id);
      return;
    }

    this.selectedId.set(id);
    this.saveSelectedEstablishment(id);
    this.queues.set([]);

    this.putContext(id).subscribe({
      next: () => this.fetchQueuesFor(id),
      error: (err) => {
        console.warn('[session] context.put error', err);
        this.fetchQueuesFor(id);
      },
    });
    this.fetchAffluenceThresholdsFor(id);
  }

  /**
   * Appelé juste après la création d'un établissement via la modale.
   * - Injecte le nouvel établissement dans la liste locale (si absent)
   * - Le sélectionne immédiatement (avec PUT /api/context)
   * - Recharge ses files
   */
  hintNewAndSelect(e: { id: string; name?: string }): void {
    if (!e?.id) return;

    const current = this.establishments();
    const exists = current.some((x) => x.id === e.id);

    // Ajoute en tête si pas présent
    if (!exists) {
      const name = (e.name ?? 'Nouvel établissement').trim() || 'Nouvel établissement';
      this.establishments.set([{ id: e.id, name }, ...current]);
    }

    // Sélection + sync back + queues
    this.selectedId.set(e.id);
    this.saveSelectedEstablishment(e.id);
    this.queues.set([]);

    this.putContext(e.id).subscribe({
      next: () => this.fetchQueuesFor(e.id),
      error: (err) => {
        console.warn('[session] context.put error (hintNewAndSelect)', err);
        this.fetchQueuesFor(e.id);
      },
    });
    this.fetchAffluenceThresholdsFor(e.id);

    // Notifie toute page qui écoute les changements (ex: accueil)
    this.bumpDataVersion();
  }

  /** NEW: à appeler après suppression d'un établissement */
  afterEstablishmentDeleted(id: string): void {
    const list = this.establishments().filter(e => e.id !== id);
    this.establishments.set(list);

    // si on a supprimé l'actuel, basculer sur le premier restant (ou null)
    if (this.selectedId() === id) {
      const next = list.length ? list[0].id : null;
      this.selectedId.set(next);
      this.saveSelectedEstablishment(next);
      this.queues.set([]);
      if (next) {
        this.putContext(next).subscribe({
          next: () => this.fetchQueuesFor(next),
          error: () => this.fetchQueuesFor(next),
        });
        this.fetchAffluenceThresholdsFor(next);
      } else {
        this.affluenceThresholds.set(null);
      }
    }
  }

  /** Rafraîchit la liste des établissements depuis le serveur */
  refreshEstablishments(): void {
    this.loadMe();
  }

  /** Réinitialise tous les signals (à appeler lors du logout) */
  reset(): void {
    this.establishments.set([]);
    this.selectedId.set(null);
    this.queues.set([]);
    this.affluenceThresholds.set(null);
    this.dataVersion.set(0);
    this.nowPromises.clear();
  }
}